import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ErrorObject } from 'ajv';
import type { JsonFormsI18nState, Translator } from '@jsonforms/core';

/**
 * AJV keywords we render a localized message for. Anything outside this set
 * falls through to AJV's own English message rather than a vague catch-all,
 * so an untranslated case stays diagnosable instead of silently generic.
 */
const LOCALIZED_ERROR_KEYWORDS = new Set([
  'required',
  'type',
  'minLength',
  'maxLength',
  'pattern',
  'format',
  'enum',
  'const',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
  'multipleOf',
  'minItems',
  'maxItems',
  'uniqueItems',
]);

/**
 * The numeric bound an AJV error carries, under whichever param name the
 * keyword uses. Feeds `{{limit}}` in the message templates.
 */
function errorLimit(error: ErrorObject | undefined): string | number | undefined {
  const params = error?.params as Record<string, unknown> | undefined;
  const limit = params?.limit ?? params?.multipleOf;
  return typeof limit === 'number' || typeof limit === 'string' ? limit : undefined;
}

/**
 * A validation-message id: `<field path>.error.<keyword>` (the field's own message, probed first) or
 * `error.<keyword>` (the keyword's).
 */
const ERROR_ID = /^(?:(.+)\.)?error\.(\w+)$/;

/**
 * The keyword's localized message, `forms.errors.<keyword>`, with the AJV limit
 * interpolated; undefined outside LOCALIZED_ERROR_KEYWORDS or when the key is missing,
 * which lets AJV's own message through.
 */
function keywordMessage(keyword: string, context: unknown, t: TFunction): string | undefined {
  if (!LOCALIZED_ERROR_KEYWORDS.has(keyword)) {
    return undefined;
  }
  const error = (context as { error?: ErrorObject } | undefined)?.error;
  // defaultValue '' would render as an empty helper text; returning
  // undefined instead lets AJV's own message through when a key is missing.
  const message = t(`forms.errors.${keyword}`, {
    defaultValue: '',
    limit: errorLimit(error),
  });
  return message === '' ? undefined : message;
}

/**
 * The field's own message for a keyword, from the owning module's `Validation:{Entity}.{field}.{keyword}` text —
 * a DSL `validation.patternMessage` is `Validation:{Entity}.{field}.pattern` (generators/csharp/field-format.ts),
 * the same text the server refuses with. Undefined when the field declares none, so the keyword's message follows.
 */
function fieldMessage(entityName: string, fieldPath: string, keyword: string, t: TFunction): string | undefined {
  const message = t(`Validation:${entityName}.${fieldPath}.${keyword}`, { defaultValue: '' });
  return message === '' ? undefined : message;
}

/**
 * Creates a JSONForms i18n configuration that resolves field labels
 * (DisplayName:Entity.field), field help texts (Description:Entity.field)
 * and validation messages from ABP localization keys.
 *
 * Usage:
 *   const i18n = useJsonFormsTranslation('Order');
 *   <JsonForms i18n={i18n} ... />
 */
export function useJsonFormsTranslation(entityName: string): JsonFormsI18nState {
  const { t } = useTranslation();

  return useMemo<JsonFormsI18nState>(() => ({
    // Cast: Translator is overloaded (string default → string return); this
    // implementation honors that contract — it only returns undefined when
    // defaultMessage is undefined.
    translate: ((id: string, defaultMessage: string | undefined, context?: unknown) => {
      // JSONForms calls translate with ids like:
      //   "#/properties/orderNumber.label"                              → field label
      //   "orderNumber.description"                                     → field help text (tooltip)
      //   "#/properties/shippingAddress/properties/street.label"        → value object nested field label
      //   "Section:Order.BasicInformation.label"                        → section group label (via i18n property)
      //   "DisplayName:Order.shippingAddress.label"                     → value object group label (via i18n property)
      //   "root.label"
      if (id.endsWith('.label')) {
        // Section/Group labels: i18n property produces "Section:Entity.Name.label"
        if (id.startsWith('Section:')) {
          const sectionKey = id.replace('.label', '');
          return t(sectionKey, { defaultValue: defaultMessage ?? id });
        }
        // DisplayName labels (value object group headers): "DisplayName:Entity.field.label"
        if (id.startsWith('DisplayName:')) {
          const displayKey = id.replace('.label', '');
          return t(displayKey, { defaultValue: defaultMessage ?? id });
        }

        const path = id.replace('#/properties/', '').replace('.label', '');
        // Top-level field labels
        if (path && !path.includes('/')) {
          return t(`DisplayName:${entityName}.${path}`, { defaultValue: defaultMessage ?? path });
        }
        // Nested value object field labels: "shippingAddress/properties/street" → "shippingAddress.street"
        if (path && path.includes('/properties/')) {
          const flatPath = path.replace(/\/properties\//g, '.');
          return t(`DisplayName:${entityName}.${flatPath}`, { defaultValue: defaultMessage ?? path });
        }
        return defaultMessage ?? id;
      }
      // Field help text (the hover tooltip). JSONForms calls translate with
      // "<path>.description" and the schema's English description as default,
      // so a missing key leaves the DSL text in place.
      if (id.endsWith('.description')) {
        const path = id.replace('#/properties/', '').replace('.description', '');
        if (path && !path.includes('/')) {
          return t(`Description:${entityName}.${path}`, { defaultValue: defaultMessage ?? '' });
        }
        if (path && path.includes('/properties/')) {
          const flatPath = path.replace(/\/properties\//g, '.');
          return t(`Description:${entityName}.${flatPath}`, { defaultValue: defaultMessage ?? '' });
        }
        return defaultMessage;
      }
      // Validation messages. JSONForms' defaultErrorTranslator probes the
      // field-specific key first ("<i18nPrefix>.error.<keyword>", prefix is
      // never empty — it falls back to "root"), then the bare generic one. The
      // field's own message answers the first when the field declares one, so it
      // wins over the keyword's; otherwise undefined sends JSONForms on to the
      // generic one, and never leaks a raw key into the UI.
      const errorId = ERROR_ID.exec(id);
      if (errorId) {
        const [, fieldPath, keyword] = errorId;
        return fieldPath === undefined ? keywordMessage(keyword, context, t) : fieldMessage(entityName, fieldPath, keyword, t);
      }
      return defaultMessage;
    }) as Translator,
  }), [entityName, t]);
}
